import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight, LogOut, Menu, Search, Settings, UserRound } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../store';
import { logoutUser } from '../../store/slices/authSlice';
import { usePermissions } from '../../hooks/usePermissions';
import { useRBAC } from '../../contexts/RBACContext';
import { useCommandPalette } from '../../contexts/CommandPaletteContext';
import NotificationBell from '../ui/NotificationBell';
import { cn } from '../../utils/cn';
import { SETTINGS_NAV_ITEM, getBreadcrumb, isNavItemVisible } from './navigation';

const focusRing = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40';

const initialsOf = (name?: string): string => {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0].charAt(0);
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
  return (first + last).toUpperCase();
};

const isMacPlatform = (): boolean =>
  typeof navigator !== 'undefined' && navigator.platform.toUpperCase().includes('MAC');

const menuItemClass = cn(
  'flex w-full items-center gap-2.5 rounded-md px-2.5 h-8 text-left text-sm text-slate-700 transition-colors',
  'hover:bg-slate-50 hover:text-slate-900',
  focusRing,
);

/** Avatar chip + dropdown (profile, settings, sign out). */
const UserMenu: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(state => state.auth);
  const { canViewUsers } = usePermissions();
  const { isLoading, hasAllPermissions, hasAnyPermission } = useRBAC();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const roleLabel = user?.roles?.[0]?.name ?? '';
  const canOpenProfile = Boolean(user?.id) && canViewUsers();
  // Same gate the Settings link had in the sidebar before it moved here.
  const canOpenSettings = isNavItemVisible(SETTINGS_NAV_ITEM, { isLoading, hasAllPermissions, hasAnyPermission });

  // Close on navigation, outside click and Escape.
  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const handlePointer = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(prev => !prev)}
        className={cn(
          'flex h-9 items-center gap-2 rounded-md pl-1 pr-1.5 transition-colors hover:bg-slate-100',
          open && 'bg-slate-100',
          focusRing,
        )}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="user-menu"
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-semibold text-indigo-700">
          {initialsOf(user?.name)}
        </span>
        <span className="hidden flex-col items-start text-left sm:flex">
          <span className="max-w-[10rem] truncate text-[13px] font-medium leading-4 text-slate-900">{user?.name || 'User'}</span>
          {roleLabel && <span className="max-w-[10rem] truncate text-xs leading-4 text-slate-500">{roleLabel}</span>}
        </span>
        <ChevronDown className="hidden h-3.5 w-3.5 text-slate-500 sm:block" aria-hidden="true" />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Account"
          className="absolute right-0 top-full z-50 mt-2 w-60 rounded-lg bg-white p-1 shadow-lg ring-1 ring-slate-900/5"
        >
          <div className="px-2.5 py-2">
            <p className="truncate text-sm font-medium text-slate-900">{user?.name || 'User'}</p>
            {user?.email && <p className="truncate text-xs text-slate-500">{user.email}</p>}
          </div>
          <div className="my-1 h-px bg-slate-100" />
          {canOpenProfile && (
            <Link role="menuitem" to={`/user/${user!.id}`} className={menuItemClass}>
              <UserRound className="h-4 w-4 text-slate-500" aria-hidden="true" />
              Your profile
            </Link>
          )}
          {canOpenSettings && (
            <Link role="menuitem" to={SETTINGS_NAV_ITEM.href} className={menuItemClass}>
              <Settings className="h-4 w-4 text-slate-500" aria-hidden="true" />
              {SETTINGS_NAV_ITEM.name}
            </Link>
          )}
          {(canOpenProfile || canOpenSettings) && <div className="my-1 h-px bg-slate-100" />}
          <button
            type="button"
            role="menuitem"
            onClick={() => dispatch(logoutUser())}
            className={cn(menuItemClass, 'hover:bg-rose-50 hover:text-rose-700')}
          >
            <LogOut className="h-4 w-4 text-slate-500" aria-hidden="true" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
};

interface HeaderProps {
  onOpenMobileMenu?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onOpenMobileMenu }) => {
  const location = useLocation();
  const { open: openCommandPalette } = useCommandPalette();
  const breadcrumb = getBreadcrumb(location.pathname);
  const shortcut = isMacPlatform() ? '⌘ K' : 'Ctrl K';

  return (
    <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-4 border-b border-slate-200 bg-white px-4 md:px-6">
      <button
        type="button"
        onClick={onOpenMobileMenu}
        className={cn('flex h-9 w-9 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100 md:hidden', focusRing)}
        aria-label="Open navigation menu"
      >
        <Menu className="h-5 w-5" />
      </button>

      <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1.5 text-sm">
        <span className="hidden truncate text-slate-500 sm:inline">{breadcrumb.section}</span>
        <ChevronRight className="hidden h-3.5 w-3.5 shrink-0 text-slate-400 sm:block" aria-hidden="true" />
        <span className="truncate font-medium text-slate-900" aria-current="page">{breadcrumb.page}</span>
      </nav>

      <div className="flex-1" />

      <button
        type="button"
        onClick={openCommandPalette}
        aria-label="Search or jump to"
        aria-keyshortcuts={isMacPlatform() ? 'Meta+K' : 'Control+K'}
        className={cn(
          'flex h-9 items-center gap-2 rounded-md text-sm text-slate-500 transition-colors',
          'w-9 justify-center sm:w-[300px] sm:max-w-[40vw] sm:justify-start sm:border sm:border-slate-200 sm:bg-slate-50 sm:pl-3 sm:pr-2',
          'hover:bg-slate-100 hover:text-slate-700',
          focusRing,
        )}
      >
        <Search className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="hidden flex-1 truncate text-left sm:inline">Search or jump to…</span>
        <kbd className="hidden rounded border border-slate-200 bg-white px-1.5 py-px font-sans text-[11px] font-medium leading-4 text-slate-600 sm:inline">
          {shortcut}
        </kbd>
      </button>

      <NotificationBell />

      <div className="hidden h-6 w-px bg-slate-200 sm:block" aria-hidden="true" />

      <UserMenu />
    </header>
  );
};

export default Header;
